import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { paletteColor } from "../lib/palette";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  Briefcase,
  Calendar,
  CalendarDays,
  ChevronDown,
  CircleCheck,
  CircleDashed,
  Clock,
  Columns3,
  Flag,
  Layers,
  List,
  ListChecks,
  MoreHorizontal,
  Pencil,
  Plus,
  SlidersHorizontal,
  Text,
  Trash2,
  TriangleAlert,
  User,
  X,
  Zap,
} from "lucide-react";
import { useStore } from "../lib/store";
import { showFeedback } from "../lib/feedback";
import { ConfirmDialog } from "./PerfilDialogs";
import type { Priority, Task, TaskProgress } from "../lib/types";
import {
  DEFAULT_TASK_VIEW,
  TASK_COLUMNS,
  TASK_PRIORITIES,
  TASK_VIEWS,
  buildTaskContexts,
  describeDue,
  filterTasks,
  groupTasksByColumn,
  isPriority,
  isTaskSort,
  isTaskViewId,
  INTERNAL_TASK_OWNER,
  DEFAULT_TASK_SORT,
  TASK_SORTS,
  matchesTaskView,
  priorityLabel,
  priorityTone,
  taskColumn,
  type TaskColumnId,
  type TaskContext,
  type TaskSortId,
  type TaskViewId,
} from "../features/tasks/board";
import { stageTone } from "../features/funnels/hub";
import { civilDate } from "../features/notifications/feed";
import { NewTaskDialog } from "../features/tasks/NewTaskDialog";
import { TIME_ZONE } from "../features/tasks/civil-time";
import { PageHead, Toolbar, ToolbarSpacer } from "../ui/PageHead";
import { SideNav } from "../ui/SideNav";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { Menu, type MenuSection } from "../ui/Menu";
import { Select } from "../ui/Select";
import { CompactSelect } from "../ui/CompactSelect";
import { Dialog } from "../ui/Dialog";
import { Field } from "../ui/Field";
import { SearchInput } from "../ui/SearchInput";
import { EmptyState } from "../ui/EmptyState";
import { Avatar } from "../ui/Avatar";
import { Count } from "../ui/Pill";
import { appPath } from "../app/routes";
import "./tarefas.css";

/**
 * Tarefas — quadro e lista.
 *
 * O quadro tem quatro colunas: três andamentos declarados pelo usuário e o
 * desfecho "Concluídas". Arrastar é conveniência de mouse; o menu de cada
 * tarefa faz o mesmo movimento por teclado, como exige a regra de
 * drag-and-drop com alternativa (AGENTS §17, §25.6).
 *
 * Criar, editar, concluir, reabrir, mover e excluir são comandos reais do
 * domínio de tarefas; editar, mudar andamento e excluir ainda não têm RPC e,
 * fora do demo, falham fechados em vez de mudar só a tela (§9, §25.1).
 *
 * Visão, funil, prioridade, modo e busca vivem na URL para que o contexto de
 * trabalho sobreviva a recarregar e a voltar de outra superfície (§3).
 */

/** Os mesmos ícones do protótipo, na mesma ordem da trilha. */
const VIEW_ICONS: Record<TaskViewId, typeof ListChecks> = {
  todas: List,
  abertas: Columns3,
  hoje: Calendar,
  semana: Clock,
  vencidas: TriangleAlert,
  concluidas: CircleCheck,
  internas: Briefcase,
};

const COLUMN_ICONS: Record<TaskColumnId, typeof CircleDashed> = {
  todo: CircleDashed,
  doing: Zap,
  waiting: Clock,
  done: CircleCheck,
};

type BoardMode = "quadro" | "lista";

/**
 * Altura do quadro a partir de onde ele começa na tela.
 *
 * O quadro precisa terminar no fim da janela para que cada coluna role por
 * dentro, como no protótipo. A distância do topo depende do cabeçalho, da
 * barra e da fonte carregada, então ela é medida em vez de chutada.
 */
function useBoardHeight() {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const measure = () => {
      const top = Math.round(node.getBoundingClientRect().top);
      node.style.setProperty("--cx-board-top", `${top}px`);
    };

    measure();
    let alive = true;
    window.addEventListener("resize", measure);
    void document.fonts?.ready.then(() => {
      if (alive) measure();
    });
    return () => {
      alive = false;
      window.removeEventListener("resize", measure);
    };
  });

  return ref;
}

export function TarefasPage() {
  const store = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [now, setNow] = useState(() => new Date().toISOString());
  const [creatingIn, setCreatingIn] = useState<TaskProgress | null>(null);
  /** Tarefa aberta para edição — o mesmo diálogo da criação. */
  const [editando, setEditando] = useState<Task>();
  const [filtering, setFiltering] = useState(false);
  const [collapsed, setCollapsed] = useState<TaskColumnId[]>([]);
  const [dragging, setDragging] = useState<string | null>(null);
  const boardRef = useBoardHeight();

  useEffect(() => {
    // Relógio de exibição: mantém "vencida" e "hoje" verdadeiros na tela. Não
    // conduz transição alguma (§27.7) — nenhuma tarefa muda por causa dele.
    const timer = window.setInterval(() => setNow(new Date().toISOString()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const view: TaskViewId = isTaskViewId(params.get("visao")) ? (params.get("visao") as TaskViewId) : DEFAULT_TASK_VIEW;
  const mode: BoardMode = params.get("modo") === "lista" ? "lista" : "quadro";
  const priority: Priority | null = isPriority(params.get("prioridade"))
    ? (params.get("prioridade") as Priority)
    : null;
  const query = params.get("q") ?? "";
  const sort = isTaskSort(params.get("ordem")) ? (params.get("ordem") as TaskSortId) : DEFAULT_TASK_SORT;
  const ownerParam = params.get("cadastro");
  const owner =
    ownerParam === INTERNAL_TASK_OWNER || store.leads.some((lead) => lead.id === ownerParam)
      ? ownerParam
      : null;
  const funnels = useMemo(() => store.funnels.filter((funnel) => !funnel.archived), [store.funnels]);
  const funnelParam = params.get("funil");
  const funnelId = funnelParam && funnels.some((funnel) => funnel.id === funnelParam) ? funnelParam : null;

  const update = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === "") next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const today = civilDate(now, TIME_ZONE);
  const contexts = useMemo(
    () => buildTaskContexts(store.tasks, store.leads, store.memberships, store.stages),
    [store.tasks, store.leads, store.memberships, store.stages],
  );

  const base = { today, now, timeZone: TIME_ZONE };
  const visible = useMemo(
    () =>
      filterTasks({ ...base, tasks: store.tasks, contexts, view, funnelId, priority, query, owner, sort }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store.tasks, contexts, view, funnelId, priority, query, owner, sort, today, now],
  );

  /** Contagem de cada visão já com funil, prioridade e busca aplicados: o
   * número ao lado do rótulo é exatamente o que a visão vai mostrar (§10). */
  const viewCounts = useMemo(() => {
    const counts = {} as Record<TaskViewId, number>;
    for (const item of TASK_VIEWS) {
      counts[item.id] = filterTasks({
        ...base,
        tasks: store.tasks,
        contexts,
        view: item.id,
        funnelId,
        priority,
        query,
        owner,
      }).length;
    }
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.tasks, contexts, funnelId, priority, query, owner, today, now]);

  const funnelCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const task of store.tasks) {
      if (!matchesTaskView(task, { ...base, view })) continue;
      if (priority && task.priority !== priority) continue;
      const id = contexts.get(task.id)?.funnelId;
      if (!id) continue;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.tasks, contexts, view, priority, today, now]);

  /** Cadastros oferecidos no filtro: só os que têm alguma tarefa. */
  const ownerOptions = useMemo(
    () =>
      store.leads
        .filter((lead) => !lead.archived && store.tasks.some((task) => task.leadId === lead.id))
        .map((lead) => ({ id: lead.id, name: lead.name, company: lead.company })),
    [store.leads, store.tasks],
  );

  const open = store.tasks.filter((task) => task.status !== "done").length;
  const dueToday = store.tasks.filter((task) => matchesTaskView(task, { ...base, view: "hoje" })).length;
  const late = store.tasks.filter((task) => matchesTaskView(task, { ...base, view: "vencidas" })).length;

  const groups = useMemo(() => groupTasksByColumn(visible), [visible]);
  const filtered =
    view !== DEFAULT_TASK_VIEW ||
    funnelId !== null ||
    priority !== null ||
    query !== "" ||
    owner !== null ||
    sort !== DEFAULT_TASK_SORT;

  const clearFilters = () =>
    update({ visao: null, funil: null, prioridade: null, q: null, cadastro: null, ordem: null });

  const moveTask = useCallback(
    async (task: Task, target: TaskColumnId) => {
      if (taskColumn(task) === target) return;
      if (target === "done") {
        await store.completeTask(task.id);
        return;
      }
      if (task.status === "done") {
        const reopened = await store.reopenTask(task.id);
        if (!reopened.ok) return;
        if (reopened.value.task.progress !== target) await store.setTaskProgress(task.id, target);
        return;
      }
      await store.setTaskProgress(task.id, target);
    },
    [store],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = (event: DragEndEvent) => {
    setDragging(null);
    const target = event.over?.id;
    const task = store.tasks.find((item) => item.id === event.active.id);
    if (!task || typeof target !== "string") return;
    void moveTask(task, target as TaskColumnId);
  };

  const sideGroups = [
    {
      // Sem rótulo: no protótipo as visões abrem a coluna, e só os funis
      // ganham um título — é o que separa os dois blocos.
      id: "visoes",
      items: TASK_VIEWS.map((item) => {
        const Icon = VIEW_ICONS[item.id];
        return {
          id: item.id,
          label: item.label,
          count: viewCounts[item.id],
          icon: <Icon size={15} aria-hidden="true" />,
        };
      }),
    },
    {
      id: "funis",
      label: "Funis",
      action: (
        <IconButton
          size="compact"
          variant="bare"
          label="Abrir a página de funis"
          icon={<Plus size={14} />}
          onClick={() => navigate(appPath("funis"))}
        />
      ),
      items: [
        { id: "todos", label: "Todos os funis", icon: <Layers size={15} aria-hidden="true" /> },
        ...funnels.map((funnel, index) => ({
          id: funnel.id,
          label: funnel.name,
          count: funnelCounts.get(funnel.id) ?? 0,
          tone: stageTone(index),
          // A marca do funil é a inicial em um quadradinho da cor dele, como
          // no protótipo: repetir o mesmo ícone em todos não distinguia nada.
          icon: (
            <i className="cx-space" aria-hidden="true">
              {funnel.name.trim().charAt(0).toUpperCase() || "?"}
            </i>
          ),
        })),
      ],
    },
  ];

  return (
    <div className="cx-layout cx-tarefas">
      <SideNav
        label="Visões e funis de tarefas"
        groups={sideGroups}
        value={funnelId ?? view}
        onChange={(id) => {
          if (isTaskViewId(id)) update({ visao: id === DEFAULT_TASK_VIEW ? null : id });
          else update({ funil: id === "todos" ? null : id });
        }}
      />
      <section className="cx-work">
        <PageHead
          title="Tarefas"
          sub={
            <>
              <b>{open}</b> em aberto · <b>{dueToday}</b> para hoje · <b>{late}</b>{" "}
              {late === 1 ? "vencida" : "vencidas"}
            </>
          }
          actions={
            <>
              <Button
                variant="ghost"
                icon={<SlidersHorizontal size={15} aria-hidden="true" />}
                onClick={() => setFiltering(true)}
              >
                Filtros e ordenação
              </Button>
              <Button
                variant="primary"
                icon={<Plus size={15} aria-hidden="true" />}
                onClick={() => setCreatingIn("todo")}
              >
                Nova tarefa
              </Button>
            </>
          }
        />
        <Toolbar label="Filtros de tarefas">
          <div className="cx-tabs" role="group" aria-label="Visualização de tarefas">
            <button
              type="button"
              className={mode === "quadro" ? "is-active" : undefined}
              aria-pressed={mode === "quadro"}
              onClick={() => update({ modo: null })}
            >
              <Columns3 size={15} aria-hidden="true" />
              Quadro
            </button>
            <button
              type="button"
              className={mode === "lista" ? "is-active" : undefined}
              aria-pressed={mode === "lista"}
              onClick={() => update({ modo: "lista" })}
            >
              <List size={15} aria-hidden="true" />
              Lista
            </button>
          </div>
          <ToolbarSpacer />
          <CompactSelect
            prefix="Prioridade"
            icon={<Flag size={13} aria-hidden="true" />}
            active={priority !== null}
            value={priority ?? "todas"}
            onChange={(value) => update({ prioridade: value === "todas" ? null : value })}
            options={[
              { value: "todas", label: "Todas" },
              ...TASK_PRIORITIES.map((item) => ({ value: item.id, label: item.label })),
            ]}
          />
          {filtered && (
            <Button
              size="compact"
              variant="ghost"
              icon={<X size={14} aria-hidden="true" />}
              onClick={clearFilters}
            >
              Limpar
            </Button>
          )}
          <SearchInput
            label="Buscar por tarefa, observação, pessoa ou empresa"
            placeholder="Buscar tarefas"
            value={query}
            onChange={(value) => update({ q: value })}
          />
        </Toolbar>

        {visible.length === 0 ? (
          <EmptyState
            variant={filtered ? "no-results" : "empty"}
            icon={<ListChecks size={22} aria-hidden="true" />}
            title={filtered ? "Nenhuma tarefa nesta seleção" : "Nenhuma tarefa por aqui"}
            description={
              filtered
                ? "Limpe os filtros ou crie uma tarefa para organizar o próximo passo."
                : "Crie a primeira tarefa para registrar o próximo passo de um lead."
            }
            action={
              <>
                {filtered && (
                  <Button variant="ghost" icon={<X size={15} aria-hidden="true" />} onClick={clearFilters}>
                    Limpar filtros
                  </Button>
                )}
                <Button
                  variant="primary"
                  icon={<Plus size={15} aria-hidden="true" />}
                  onClick={() => setCreatingIn("todo")}
                >
                  Nova tarefa
                </Button>
              </>
            }
          />
        ) : mode === "quadro" ? (
          <DndContext
            sensors={sensors}
            onDragStart={(event: DragStartEvent) => setDragging(String(event.active.id))}
            onDragCancel={() => setDragging(null)}
            onDragEnd={onDragEnd}
          >
            <div className="cx-board" ref={boardRef}>
              {TASK_COLUMNS.map((column) => (
                <BoardColumn
                  key={column.id}
                  column={column}
                  tasks={groups[column.id]}
                  contexts={contexts}
                  base={base}
                  dragging={dragging}
                  onCreate={column.id === "done" ? undefined : () => setCreatingIn(column.id as TaskProgress)}
                  onMove={moveTask}
                  onOpenLead={(leadId) => navigate(`${appPath("leads")}/${leadId}`)}
                  onEdit={setEditando}
                />
              ))}
            </div>
          </DndContext>
        ) : (
          <div className="cx-tl">
            {TASK_COLUMNS.map((column) => {
              const items = groups[column.id];
              if (items.length === 0) return null;
              const isCollapsed = collapsed.includes(column.id);
              return (
                <ListGroup
                  key={column.id}
                  column={column}
                  tasks={items}
                  contexts={contexts}
                  base={base}
                  collapsed={isCollapsed}
                  onToggle={() =>
                    setCollapsed((current) =>
                      current.includes(column.id)
                        ? current.filter((id) => id !== column.id)
                        : [...current, column.id],
                    )
                  }
                  onCreate={column.id === "done" ? undefined : () => setCreatingIn(column.id as TaskProgress)}
                  onMove={moveTask}
                  onOpenLead={(leadId) => navigate(`${appPath("leads")}/${leadId}`)}
                  onEdit={setEditando}
                />
              );
            })}
          </div>
        )}
      </section>

      <TaskFiltersDialog
        open={filtering}
        owner={owner}
        sort={sort}
        leads={ownerOptions}
        onClose={() => setFiltering(false)}
        onApply={(next) =>
          update({
            cadastro: next.owner,
            ordem: next.sort === DEFAULT_TASK_SORT ? null : next.sort,
          })
        }
      />

      <NewTaskDialog
        open={creatingIn !== null}
        progress={creatingIn ?? "todo"}
        onClose={() => setCreatingIn(null)}
      />

      {/* Editar usa o mesmo formulário da criação: mesmos campos, mesma
          validação, mesmas mensagens (§14.1). */}
      <NewTaskDialog
        open={editando !== undefined}
        progress={editando?.progress ?? "todo"}
        task={editando}
        onClose={() => setEditando(undefined)}
      />
    </div>
  );
}

interface ColumnMeta {
  id: TaskColumnId;
  label: string;
  tone: string;
}

interface SurfaceProps {
  column: ColumnMeta;
  tasks: Task[];
  contexts: Map<string, TaskContext>;
  base: { today: string; now: string; timeZone: string };
  onCreate?: () => void;
  onMove: (task: Task, target: TaskColumnId) => void | Promise<void>;
  onOpenLead: (leadId: string) => void;
  onEdit: (task: Task) => void;
}

function BoardColumn({
  column,
  tasks,
  contexts,
  base,
  dragging,
  onCreate,
  onMove,
  onOpenLead,
  onEdit,
}: SurfaceProps & { dragging: string | null }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const Icon = COLUMN_ICONS[column.id];
  return (
    <section
      ref={setNodeRef}
      className="cx-col"
      data-over={isOver && dragging ? "true" : undefined}
      style={{ "--cx-tone": column.tone } as CSSProperties}
      aria-label={`${column.label}, ${tasks.length} ${tasks.length === 1 ? "tarefa" : "tarefas"}`}
    >
      <header className="cx-col-head">
        <span className="cx-status-pill">
          <Icon size={14} aria-hidden="true" />
          {column.label}
        </span>
        <Count value={tasks.length} label={`${tasks.length === 1 ? "tarefa" : "tarefas"} em ${column.label}`} />
        {onCreate && (
          <IconButton
            className="cx-col-add"
            size="compact"
            variant="bare"
            label={`Adicionar tarefa em ${column.label}`}
            icon={<Plus size={15} />}
            onClick={onCreate}
          />
        )}
      </header>
      <div className="cx-col-body">
        <div className="cx-cards">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              context={contexts.get(task.id)}
              base={base}
              onMove={onMove}
              onOpenLead={onOpenLead}
              onEdit={onEdit}
            />
          ))}
        </div>
        {onCreate && (
          <button type="button" className="cx-add-task" onClick={onCreate}>
            <Plus size={15} aria-hidden="true" />
            Adicionar tarefa
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * Menu "…" da tarefa, no formato do protótipo: o nome em cima, os andamentos
 * com o ponto da coluna e o atual marcado, e depois editar e excluir.
 * "Concluídas" é andamento como os outros aqui — escolher é concluir, e sair
 * dela é reabrir, pelo mesmo `moveTask` do arraste.
 */
function taskMenuSections(task: Task): MenuSection[] {
  const current = taskColumn(task);
  return [
    {
      id: "andamento",
      label: "Andamento",
      items: TASK_COLUMNS.map((item) => ({
        id: `mover:${item.id}`,
        label: item.label,
        icon: <i className="cx-menu-dot" />,
        tone: item.tone,
        selected: current === item.id,
      })),
    },
    {
      id: "acoes",
      items: [
        { id: "editar", label: "Editar tarefa", icon: <Pencil size={14} /> },
        { id: "excluir", label: "Excluir tarefa", icon: <Trash2 size={14} />, danger: true },
      ],
    },
  ];
}

function useTaskMenu(task: Task, onMove: SurfaceProps["onMove"], onEdit: SurfaceProps["onEdit"]) {
  const store = useStore();
  const [deleting, setDeleting] = useState(false);
  const onSelect = useCallback(
    (id: string) => {
      if (id === "editar") onEdit(task);
      else if (id === "excluir") setDeleting(true);
      else if (id.startsWith("mover:")) void onMove(task, id.slice("mover:".length) as TaskColumnId);
    },
    [onEdit, onMove, task],
  );
  const dialog = deleting ? (
    <ConfirmDialog
      title="Excluir tarefa"
      question={`“${task.description}” sai do quadro e da lista.${task.leadId ? " A jornada do cadastro registra a exclusão." : ""}`}
      confirmLabel="Excluir tarefa"
      danger
      onConfirm={() => {
        setDeleting(false);
        void store.deleteTask(task.id).then((result) => {
          if (result.ok) showFeedback("Tarefa excluída.", "success");
        });
      }}
      onClose={() => setDeleting(false)}
    />
  ) : null;
  return { onSelect, dialog };
}

interface TaskItemProps {
  task: Task;
  context?: TaskContext;
  base: { today: string; now: string; timeZone: string };
  onMove: SurfaceProps["onMove"];
  onOpenLead: SurfaceProps["onOpenLead"];
  onEdit: SurfaceProps["onEdit"];
}

function useToggle() {
  const store = useStore();
  return useCallback(
    (task: Task) => {
      if (task.status === "done") void store.reopenTask(task.id);
      else void store.completeTask(task.id);
    },
    [store],
  );
}

function CompleteButton({ task, onToggle }: { task: Task; onToggle: (task: Task) => void }) {
  const done = task.status === "done";
  return (
    <button
      type="button"
      className="cx-complete"
      aria-pressed={done}
      aria-label={`${done ? "Reabrir" : "Concluir"} ${task.description}`}
      title={done ? "Reabrir tarefa" : "Concluir tarefa"}
      onClick={() => onToggle(task)}
    >
      {done ? <CircleCheck size={16} aria-hidden="true" /> : <CircleDashed size={16} aria-hidden="true" />}
    </button>
  );
}

function PersonRow({ task, context }: { task: Task; context?: TaskContext }) {
  const lead = context?.lead;
  // Três estados distintos: a tarefa da operação, que não tem cadastro nenhum;
  // a tarefa de um cadastro que não está carregado; e o cadastro em si.
  if (!task.leadId)
    return (
      <div className="cx-row" title="Tarefa interna, sem cadastro vinculado">
        <User size={16} aria-hidden="true" className="cx-row-ico" />
        <span className="cx-internal" aria-hidden="true">
          <Briefcase size={11} />
        </span>
        <span>Interna</span>
      </div>
    );
  if (!lead)
    return (
      <div className="cx-row" title="O cadastro desta tarefa não está carregado">
        <User size={16} aria-hidden="true" className="cx-row-ico" />
        <span className="is-sub">Cadastro indisponível</span>
      </div>
    );
  return (
    <div className="cx-row" title={lead.company ? `${lead.name} · ${lead.company}` : lead.name}>
      <Avatar name={lead.name} photoUrl={lead.photoUrl} className="cx-mini-ava" />
      <span>
        <b>{lead.name}</b>
      </span>
    </div>
  );
}

function DueRow({ task, base }: { task: Task; base: TaskItemProps["base"] }) {
  const due = describeDue(task, { ...base, view: "todas" });
  return (
    <div className={`cx-row${due.late ? " is-late" : due.today ? " is-today" : ""}`} title={due.title}>
      <CalendarDays size={16} aria-hidden="true" className="cx-row-ico" />
      <span>
        <b>{due.label}</b> · {due.time}
        {due.late ? " · vencida" : ""}
      </span>
    </div>
  );
}

function PriorityRow({ task }: { task: Task }) {
  return (
    <div className="cx-row" style={{ "--cx-pri": priorityTone(task.priority) } as CSSProperties}>
      <Flag size={16} aria-hidden="true" className="cx-row-ico cx-flag" />
      <span>{priorityLabel(task.priority)}</span>
    </div>
  );
}

function StageRow({ context }: { context?: TaskContext }) {
  const stage = context?.stage;
  if (!stage) return null;
  return (
    <div className="cx-row">
      <Layers size={16} aria-hidden="true" className="cx-row-ico" />
      <span className="cx-fpill" style={{ "--cx-tag": paletteColor(stage.color) } as CSSProperties}>
        {stage.name}
      </span>
    </div>
  );
}

function TaskCard({ task, context, base, onMove, onOpenLead, onEdit }: TaskItemProps) {
  const toggle = useToggle();
  const { onSelect, dialog } = useTaskMenu(task, onMove, onEdit);
  // O card inteiro arrasta pelo ponteiro; `attributes` ficam de fora de
  // propósito, porque eles tornariam o <article> um botão focável. A
  // alternativa de teclado é o menu de ações, não um arraste por teclado.
  const { listeners, setNodeRef, isDragging } = useDraggable({ id: task.id });

  return (
    <article
      ref={setNodeRef}
      {...listeners}
      className={`cx-card${task.status === "done" ? " is-done" : ""}${isDragging ? " is-dragging" : ""}`}
      aria-label={task.description}
    >
      <div className="cx-card-head">
        <button
          type="button"
          className="cx-card-title"
          disabled={!task.leadId}
          onClick={() => task.leadId && onOpenLead(task.leadId)}
          title={task.leadId ? `Abrir o lead de ${task.description}` : "Tarefa interna: não pertence a um cadastro."}
        >
          {task.description}
        </button>
        {task.note && (
          <span className="cx-card-desc-ico" role="img" aria-label="Com observação" title={task.note}>
            <Text size={16} aria-hidden="true" />
          </span>
        )}
        <div className="cx-card-tools">
          <CompleteButton task={task} onToggle={toggle} />
          <IconButton
            size="compact"
            variant="bare"
            label={`Editar ${task.description}`}
            icon={<Pencil size={14} aria-hidden="true" />}
            onClick={() => onEdit(task)}
          />
          <Menu
            label={`Ações de ${task.description}`}
            title={task.description}
            sections={taskMenuSections(task)}
            onSelect={onSelect}
            trigger={(args) => (
              <IconButton
                {...args}
                size="compact"
                variant="bare"
                label={`Ações de ${task.description}`}
                icon={<MoreHorizontal size={15} />}
              />
            )}
          />
          {dialog}
        </div>
      </div>
      <div className="cx-rows">
        <PersonRow task={task} context={context} />
        <DueRow task={task} base={base} />
        <PriorityRow task={task} />
        <StageRow context={context} />
      </div>
    </article>
  );
}

function ListGroup({
  column,
  tasks,
  contexts,
  base,
  collapsed,
  onToggle,
  onCreate,
  onMove,
  onOpenLead,
  onEdit,
}: SurfaceProps & { collapsed: boolean; onToggle: () => void }) {
  const Icon = COLUMN_ICONS[column.id];
  const bodyId = `tl-${column.id}`;
  return (
    <section className="cx-tl-group" style={{ "--cx-tone": column.tone } as CSSProperties}>
      <div className="cx-tl-head">
        <IconButton
          size="compact"
          variant="bare"
          label={`${collapsed ? "Expandir" : "Recolher"} ${column.label}`}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          icon={
            <ChevronDown size={14} className={collapsed ? "cx-tl-chevron is-collapsed" : "cx-tl-chevron"} />
          }
          onClick={onToggle}
        />
        <span className="cx-status-pill">
          <Icon size={14} aria-hidden="true" />
          {column.label}
        </span>
        <Count value={tasks.length} label={`${tasks.length === 1 ? "tarefa" : "tarefas"} em ${column.label}`} />
      </div>
      <div id={bodyId} hidden={collapsed}>
        <div className="cx-tl-cols" aria-hidden="true">
          <span />
          <span>Nome</span>
          <span>Lead</span>
          <span>Prazo</span>
          <span>Prioridade</span>
          <span>Etapa</span>
          <span />
        </div>
        {tasks.map((task, index) => (
          <ListRow
            key={task.id}
            task={task}
            context={contexts.get(task.id)}
            base={base}
            last={index === tasks.length - 1}
            onMove={onMove}
            onEdit={onEdit}
            onOpenLead={onOpenLead}
          />
        ))}
        {onCreate && (
          <div className="cx-tl-foot">
            <button type="button" className="cx-tl-add" onClick={onCreate}>
              <Plus size={14} aria-hidden="true" />
              Adicionar tarefa
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function ListRow({ task, context, base, last, onMove, onOpenLead, onEdit }: TaskItemProps & { last: boolean }) {
  const toggle = useToggle();
  const { onSelect, dialog } = useTaskMenu(task, onMove, onEdit);
  return (
    <div className={`cx-tl-row${last ? " is-last" : ""}${task.status === "done" ? " is-done" : ""}`}>
      <div className="cx-tl-gutter">
        <CompleteButton task={task} onToggle={toggle} />
      </div>
      <div className="cx-tl-name">
        <button
          type="button"
          className="cx-card-title"
          disabled={!task.leadId}
          onClick={() => task.leadId && onOpenLead(task.leadId)}
          title={task.leadId ? `Abrir o lead de ${task.description}` : "Tarefa interna: não pertence a um cadastro."}
        >
          {task.description}
        </button>
      </div>
      <div className="cx-tl-cell">
        <PersonRow task={task} context={context} />
      </div>
      <div className="cx-tl-cell">
        <DueRow task={task} base={base} />
      </div>
      <div className="cx-tl-cell">
        <PriorityRow task={task} />
      </div>
      <div className="cx-tl-cell">
        <StageRow context={context} />
      </div>
      <div className="cx-tl-menu">
        <Menu
          label={`Ações de ${task.description}`}
          title={task.description}
          sections={taskMenuSections(task)}
          onSelect={onSelect}
          trigger={(args) => (
            <IconButton
              {...args}
              size="compact"
              variant="bare"
              label={`Ações de ${task.description}`}
              icon={<MoreHorizontal size={15} />}
            />
          )}
        />
        {dialog}
      </div>
    </div>
  );
}

export default TarefasPage;

/**
 * Filtros e ordenação de Tarefas.
 *
 * Cadastro e ordem ficam aqui porque são escolhas de sessão, não de leitura
 * rápida: a barra guarda o que se troca o tempo todo (visão, prioridade,
 * busca). Aplicar escreve na URL, então a seleção sobrevive ao reload e pode
 * ser compartilhada (AGENTS §3).
 */
function TaskFiltersDialog({
  open,
  owner,
  sort,
  leads,
  onClose,
  onApply,
}: {
  open: boolean;
  owner: string | null;
  sort: TaskSortId;
  leads: Array<{ id: string; name: string; company?: string }>;
  onClose: () => void;
  onApply: (next: { owner: string | null; sort: TaskSortId }) => void;
}) {
  const [draftOwner, setDraftOwner] = useState(owner ?? "todos");
  const [draftSort, setDraftSort] = useState<TaskSortId>(sort);

  useEffect(() => {
    if (!open) return;
    setDraftOwner(owner ?? "todos");
    setDraftSort(sort);
  }, [open, owner, sort]);

  const dirty = draftOwner !== (owner ?? "todos") || draftSort !== sort;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title="Filtros de tarefas"
      description="Vale para o quadro e para a lista."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onApply({ owner: draftOwner === "todos" ? null : draftOwner, sort: draftSort });
              onClose();
            }}
          >
            Aplicar filtros
          </Button>
        </>
      }
    >
      <div className="cx-form">
        <Field label="Cadastro">
          {({ id }) => (
            <Select
              id={id}
              label="Cadastro"
              size="field"
              value={draftOwner}
              onChange={setDraftOwner}
              options={[
                { value: "todos", label: "Todos" },
                { value: INTERNAL_TASK_OWNER, label: "Internas", hint: "Trabalho da operação" },
                ...leads.map((lead) => ({ value: lead.id, label: lead.name, hint: lead.company })),
              ]}
            />
          )}
        </Field>
        <Field label="Ordenar por">
          {({ id }) => (
            <Select
              id={id}
              label="Ordenar por"
              size="field"
              value={draftSort}
              onChange={(value) => setDraftSort(value as TaskSortId)}
              options={TASK_SORTS.map((item) => ({ value: item.id, label: item.label }))}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
